import { BookOpen, Users, Calendar, Award, MapPin, Clock, Star, Heart, Globe, TreePine, CheckCircle, Briefcase, GraduationCap, Droplets, Baby, BookOpenCheck, Sprout, Handshake } from "lucide-react";

const timelineEvents = [
  {
    year: "1925",
    date: "31 de octubre",
    title: "La Semilla Rotaria",
    description: "Visita del delegado internacional James H. Roth a Zaragoza, marcando el inicio del movimiento rotario en la ciudad.",
    location: "Posada de las Almas"
  },
  {
    year: "1925",
    date: "11 de noviembre",
    title: "Solicitud Formal de Adhesión",
    description: "Solicitud formal de adhesión como Rotary Club de Zaragoza a Rotary International.",
    location: "Zaragoza",
    highlight: true
  },
  {
    year: "1925",
    date: "28 de diciembre",
    title: "Carta de Constitución",
    description: "Se recibió en Zaragoza la carta de constitución con el número 2193.",
    location: "Zaragoza",
    highlight: true
  },
  {
    year: "1927",
    date: "8 de abril",
    title: "Expansión Regional",
    description: "Zaragoza promueve la formación del Club de Logroño, expandiendo la presencia rotaria en Aragón.",
    location: "Casa Lac"
  },
  {
    year: "1931",
    date: "6-13 de mayo",
    title: "IV Conferencia del Distrito 60",
    description: "Zaragoza acoge un evento de varios días que reunió a rotarios de toda España.",
    location: "Gran Hotel, El Pilar, La Seo",
    highlight: true
  },
  {
    year: "1930-1932",
    date: "",
    title: "Ciclo de Conferencias",
    description: "Intenso ciclo de conferencias culturales, científicas y sociales sobre minería, genética, economía forestal, urbanismo y arte.",
    location: "Gran Hotel"
  },
  {
    year: "2025",
    date: "",
    title: "Centenario",
    description: "Celebración del centenario del Rotary Club de Zaragoza, inauguración del Reloj Solar y conexión con el Eclipse Total de agosto 2026.",
    location: "Zaragoza",
    highlight: true
  }
];

const keyFigures = [
  "Miguel Allúe Salvador (Alcalde)",
  "Aurelio Grasa",
  "Miguel Á. Navarro",
  "José Mª Bascones",
  "Celestino Archanco",
  "Luis Recasens",
  "Mariano Gros",
  "Juan A. Gallart"
];

const areasInteres = [
  { icon: Handshake, titulo: "Paz y Prevención de Conflictos", descripcion: "Fomentamos el diálogo para construir comunidades estables." },
  { icon: Heart, titulo: "Prevención y Tratamiento de Enfermedades", descripcion: "Lideramos la iniciativa global PolioPlus." },
  { icon: Droplets, titulo: "Agua, Saneamiento e Higiene", descripcion: "Facilitamos el acceso a recursos vitales." },
  { icon: Baby, titulo: "Salud Materno-Infantil", descripcion: "Protegemos a los más vulnerables." },
  { icon: GraduationCap, titulo: "Alfabetización y Educación Básica", descripcion: "Abrimos puertas a través del conocimiento." },
  { icon: Briefcase, titulo: "Desarrollo Económico Comunitario", descripcion: "Creamos oportunidades de empleo y emprendimiento." },
  { icon: Sprout, titulo: "Medio Ambiente", descripcion: "Protegemos nuestro planeta para las futuras generaciones." },
];

const proyectosZaragoza = [
  "End Polio Now – Campaña global para erradicar la polio.",
  "ATAIDES – Producción de fundas para mascarillas.",
  "Premio Don Bosco – Apoyo a jóvenes innovadores.",
  "Fundación La Caridad – Equipamiento de aulas con material informático y libros.",
  "Proyectos en Camerún e India – Apoyo a la educación y el empoderamiento femenino.",
  "Casa Cuna Ain Karen – Ayuda a niños y mujeres en riesgo de exclusión.",
  "Premio de la Paz – Fomento de la paz entre Zaragoza y Pau.",
  "Premio a la Excelencia Profesional – Reconocimiento a la integridad y mérito profesional.",
  "Rotary Youth Exchanges – Intercambio internacional para jóvenes de 15 a 18 años.",
  "Ciclo de conferencias – Espacios de debate y aprendizaje.",
  "Bosque Rotario – Iniciativa ambiental para la conservación de espacios verdes."
];

const Historia = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="nuestro-club-page">
      {/* Header */}
      <header className="bg-rotary-gradient py-16 md:py-24 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 glass-dark px-4 py-2 rounded-full mb-6">
            <Users className="w-4 h-4 text-[#F7A81B]" />
            <span className="text-white/80 text-sm font-heading uppercase tracking-wider">
              Nuestro Club
            </span>
          </div>
          <h1 className="font-bold-sans text-4xl sm:text-5xl md:text-6xl text-white mb-4 tracking-tight">
            100 Años de Servicio
          </h1>
          <p className="text-white/70 text-base md:text-lg max-w-2xl mx-auto">
            Una década de compromiso, ideas y presencia en la ciudad
          </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-12 md:py-20 px-4">
        <div className="max-w-5xl mx-auto">
          
          {/* BLOQUE 1: Qué es Rotary Internacional y nuestra Historia */}
          <section className="mb-16 md:mb-24" data-testid="rotary-internacional">
            <div className="glass-panel p-8 md:p-12 rounded-3xl">
              <div className="divider-gold mb-8" />
              <h2 className="font-heading text-2xl md:text-3xl text-[#013E7F] mb-6 text-center">
                Qué es Rotary Internacional y nuestra Historia
              </h2>
              
              {/* ¿Qué es Rotary International? */}
              <div className="mb-10">
                <h3 className="font-heading text-xl text-[#F7A81B] mb-4">
                  ¿Qué es Rotary International?
                </h3>
                <p className="text-slate-600 italic mb-4 text-lg">
                  Unidos por el servicio, impulsados por la integridad.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  Rotary International es una red global de 1.4 millones de vecinos, amigos y líderes comunitarios 
                  que ven un mundo donde las personas se unen y toman acción para generar un cambio duradero. 
                  Somos la primera organización de servicio voluntario del mundo, sin filiaciones políticas ni 
                  religiosas, dedicada a abordar los desafíos más urgentes de la humanidad a través de la amistad 
                  y la ética profesional.
                </p>
              </div>
              
              {/* Historia de Rotary International */}
              <div>
                <h3 className="font-heading text-xl text-[#F7A81B] mb-4">
                  Historia de Rotary International
                </h3>
                <p className="text-slate-600 italic mb-4 text-lg">
                  Más de un siglo transformando el mundo.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  Todo comenzó el 23 de febrero de 1905, cuando el abogado Paul Harris fundó el primer club 
                  en Chicago con una idea simple: que profesionales de distintos sectores pudieran intercambiar 
                  ideas y forjar amistades significativas.
                </p>
                <p className="text-slate-600 leading-relaxed mt-4">
                  Lo que empezó como una reunión de amigos pronto se convirtió en un movimiento global. En 1921, 
                  Rotary se extendió por los cinco continentes y, desde entonces, nuestra historia ha estado 
                  marcada por hitos como la lucha por la erradicación de la polio y el apoyo a la paz mundial 
                  a través de nuestra estrecha colaboración con las Naciones Unidas.
                </p>
              </div>
            </div>
          </section>

          {/* BLOQUE 2: Áreas de Interés y Proyectos */}
          <section className="mb-16 md:mb-24 bg-gradient-to-br from-amber-50/50 to-orange-50/30 -mx-4 px-4 py-12 md:py-16 rounded-3xl" data-testid="areas-interes">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-10">
                <div className="divider-gold mb-6" />
                <h2 className="font-heading text-2xl md:text-3xl text-[#013E7F] mb-4">
                  Áreas de Interés y Proyectos
                </h2>
                <p className="text-slate-600 italic text-lg">
                  Acciones que cambian vidas.
                </p>
                <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
                  En Rotary no solo soñamos con un mundo mejor, lo construimos. Concentramos nuestros esfuerzos 
                  en siete áreas estratégicas que garantizan un impacto real y sostenible:
                </p>
              </div>
              
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {areasInteres.map((area, index) => {
                  const Icon = area.icon;
                  return (
                    <div 
                      key={index}
                      className="glass-panel p-5 rounded-xl hover:border-[#F7A81B]/50 transition-all duration-300"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-[#013E7F]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Icon className="w-5 h-5 text-[#013E7F]" />
                        </div>
                        <div>
                          <h4 className="font-heading font-semibold text-slate-800 mb-1">
                            {area.titulo}
                          </h4>
                          <p className="text-slate-600 text-sm">
                            {area.descripcion}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* BLOQUE 3: Rotary Club Zaragoza */}
          <section className="mb-16 md:mb-24" data-testid="rotary-zaragoza">
            <div className="glass-panel p-8 md:p-12 rounded-3xl">
              <div className="divider-gold mb-8" />
              <h2 className="font-heading text-2xl md:text-3xl text-[#013E7F] mb-6 text-center">
                Rotary Club Zaragoza
              </h2>
              <p className="text-slate-600 italic mb-4 text-lg text-center">
                Compromiso local con visión global.
              </p>
              
              <p className="text-slate-600 leading-relaxed mb-4">
                El Rotary Club de Zaragoza, fundado en 1925, ha estado presente en algunos de los momentos 
                más emblemáticos de la vida social y cultural de la ciudad.
              </p>
              <p className="text-slate-600 leading-relaxed mb-6">
                El Rotary Club Zaragoza es el corazón de este movimiento en nuestra ciudad. Somos un grupo 
                diverso de profesionales y líderes locales unidos por un objetivo común: devolver a Zaragoza 
                parte de lo que nos ha dado.
              </p>
              <p className="text-slate-600 leading-relaxed mb-6">
                Desde 1925, el Rotary Club de Zaragoza impulsa iniciativas solidarias en colaboración con 
                diversas organizaciones. Algunos de nuestros proyectos incluyen:
              </p>
              
              <div className="grid sm:grid-cols-2 gap-3">
                {proyectosZaragoza.map((proyecto, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-3 p-3 bg-white/50 rounded-xl"
                  >
                    <CheckCircle className="w-5 h-5 text-[#F7A81B] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 text-sm">{proyecto}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Centenario Badge */}
          <section className="mb-16 md:mb-24">
            <div className="glass-countdown rounded-3xl p-8 md:p-12 text-center relative overflow-hidden">
              <div className="absolute inset-0 bg-eclipse-glow opacity-50" />
              <div className="relative z-10">
                <div className="inline-flex items-center justify-center w-28 h-28 md:w-36 md:h-36 rounded-full border-4 border-[#F7A81B]/50 mb-6 glass-countdown">
                  <div className="text-center">
                    <Star className="w-6 h-6 text-[#F7A81B] mx-auto mb-1" />
                    <span className="font-bold-sans text-4xl md:text-5xl text-white block">100</span>
                    <span className="text-xs text-[#F7A81B] uppercase tracking-wider">Años</span>
                  </div>
                </div>
                <h3 className="font-accent text-2xl md:text-3xl text-white mb-4">
                  1925 - 2025
                </h3>
                <p className="text-white/60 font-heading uppercase tracking-widest text-sm">
                  Archivo Histórico de Prensa
                </p>
              </div>
            </div>
          </section>

          {/* Timeline */}
          <section className="mb-16 md:mb-24" data-testid="historia-timeline">
            <div className="text-center mb-12">
              <div className="divider-gold mb-6" />
              <h2 className="font-heading text-2xl md:text-3xl text-slate-800">
                Línea del Tiempo
              </h2>
            </div>
            
            <div className="relative">
              {/* Vertical Line */}
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#013E7F] via-[#F7A81B] to-[#013E7F]" />
              
              {timelineEvents.map((event, index) => (
                <div 
                  key={index}
                  className={`relative mb-8 md:mb-12 ${index % 2 === 0 ? 'md:pr-1/2 md:text-right' : 'md:pl-1/2 md:ml-auto'}`}
                  data-testid={`timeline-event-${index}`}
                >
                  <div className={`pl-12 md:pl-0 ${index % 2 === 0 ? 'md:pr-12' : 'md:pl-12'}`}>
                    {/* Timeline Dot */}
                    <div className={`absolute left-2 md:left-1/2 transform md:-translate-x-1/2 w-5 h-5 rounded-full border-4 ${
                      event.highlight 
                        ? 'bg-[#F7A81B] border-[#F7A81B]/30 shadow-lg shadow-amber-500/30' 
                        : 'bg-white border-[#013E7F]'
                    }`} />
                    
                    {/* Content Card */}
                    <div className={`card-glass p-6 rounded-2xl ${event.highlight ? 'border-[#F7A81B]/30 bg-amber-50/50' : ''}`}>
                      <div className={`flex items-center gap-2 mb-2 ${index % 2 === 0 ? 'md:justify-end' : ''}`}>
                        <span className="font-accent text-2xl text-[#013E7F]">{event.year}</span>
                        {event.date && (
                          <span className="text-slate-500 text-sm">- {event.date}</span>
                        )}
                      </div>
                      <h3 className="font-heading text-lg font-semibold text-slate-800 mb-2">
                        {event.title}
                      </h3>
                      <p className="text-slate-600 text-sm mb-3">
                        {event.description}
                      </p>
                      {event.location && (
                        <div className={`flex items-center gap-1 text-xs text-slate-500 ${index % 2 === 0 ? 'md:justify-end' : ''}`}>
                          <MapPin className="w-3 h-3" />
                          <span>{event.location}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Key Figures */}
          <section className="mb-16 md:mb-24" data-testid="historia-figures">
            <div className="text-center mb-10">
              <div className="divider-gold mb-6" />
              <h2 className="font-heading text-2xl md:text-3xl text-slate-800 mb-4">
                Figuras Destacadas
              </h2>
              <p className="text-slate-600">
                Personalidades que marcaron los primeros años del club
              </p>
            </div>
            
            <div className="flex flex-wrap justify-center gap-3">
              {keyFigures.map((figure, index) => (
                <span 
                  key={index}
                  className="glass-panel px-4 py-2 rounded-full text-sm text-slate-700 font-medium hover:bg-[#F7A81B]/10 hover:text-[#013E7F] transition-all duration-300 cursor-default"
                >
                  {figure}
                </span>
              ))}
            </div>
          </section>

          {/* Lema */}
          <section className="text-center">
            <div className="bg-rotary-gradient rounded-3xl p-8 md:p-12">
              <p className="font-accent text-xl md:text-2xl text-white/90 italic mb-4">
                "Dar de sí antes de pensar en sí"
              </p>
              <p className="text-white/60 text-sm font-heading uppercase tracking-widest">
                Lema eterno del espíritu rotario
              </p>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default Historia;
